/* #region Search Reducer Description */
/** Search Reducer
 *
 * searchWithin is the place option associated with the clicked "places within " button.
 * searchByLocation is the lat,lon distance and distance type ("Miles", "Kilometers") to search from within.
 * confidenceLevel: is one of the following user confidence levels ["ACCEPTED", "PROVISIONAL", "CERTIFIED"].
 * directChildrenOnly: whether to filter on direct children in search results.
 * placeType: If included only include places matching placeType.id and placeType.label.
 * yearRange: If included filter results by year range.
 * startYear: Used to formulate yearRange.
 * endYear: Used to formulate yearRange.
 * exactYear: If included filter results by exact year.
 *
 * Places 2.0 request params see (https://icseng.atlassian.net/wiki/x/nYP2Bw)
 *  -- BASIC TEXT SEARCH --
 * text: string, (e.g "Salt Lake City")
 *
 * -- SEARCH WITHIN --
 * reqParents: string,  Parent place Id (e.g. "1" for United States)
 *
 * -- FILTER DIRECT CHILDREN --
 * reqDirParents: string,
 *
 * -- FILTER BY CONFIDENCE LEVEL
 * valType: string, [e.g "non_val_only", or "val_only"]
 * pubType: string, [e.g "non_pub_only"]
 *
 * -- FILTER BY YEAR RANGE
 * reqYears: string, [e.g. "1800-1900"]
 *
 * --- SEARCH BY LOCATION
 * lng: string, latitude (e.g. "115.00")
 * lat: string, longitude (e.g. "-45,00")
 * distance: string  --- SEARCH BY LOCATION includes distance type character 'm' for Miles and 'km" for Kilometers (eg. '15m')
 *
 * -- SHOW PLACES WITHIN Flag --
 * includeIsParent: boolean, [e.g. "true"]
 */
/* #endregion */

import type { Dispatch } from 'react'
import debugFactory, { debugVerbose as debugVerboseFactory } from '@fs/zion-debug'

const debug = debugFactory('standards:places:searchReducer')
const debugVerbose = debugVerboseFactory('standards:places:searchReducer')

// Neighbor summary hydrated by fetchBorderingBoundaries (name/type for the Nearby Places list)
export type BorderingSummary = { id: string | number; name?: string; typeId?: number }

// Bordering overlay (maps layout): the anchor's border neighbors, fetched when
// the `bordering` URL param is set. Orthogonal to the search result set — the
// overlay and an active text/radius search coexist.
export type BorderingNeighbor = {
  id: string | number
  fullName: string
  type?: string
  date?: string
  latitude?: number
  longitude?: number
}
export type BorderingOverlay = {
  anchorId: string
  anchorName?: string
  neighbors: BorderingNeighbor[]
  // The anchor has no boundary of its own, so borders can't be computed
  noBoundary?: boolean
  isLoading: boolean
}

/* #region Default Values */
const SEARCH_DEFAULT = {
  criteria: {
    /**
     * May contain any combination of the following fields:
     *        KEY | CORRESPONDING INPUT(s)
     * --------------------------------------
     *       text | Place Name
     * reqParents | Places Within
     *   reqTypes | Place Type
     *   reqYears | Exact Year, Year Start, Year End
     *        lat | (from "Set Location" feature)
     *        lng | (from "Set Location" feature)
     *   distance | Distance from Location (input and dropdown values are combined)
     */
  },
  isLoading: false,
  noSearchesPerformed: true,
  validResults: true,
  results: undefined,
  selectedPlace: undefined,
  toggleToUnfocused: false,
  idList: [],
  resultCount: 0,
  resultsFound: 0,
  filterOverlayIsOpen: false,
  // Bordering overlay state — null while the `bordering` URL param is absent
  bordering: null,
  // std-17864: bumped when an already-selected place is clicked again, to re-display its map
  // popover without changing the selection (the popover is a transient view of the selection).
  popoverReopenNonce: 0,
}

const DETAILS_DEFAULT = {
  details: {
    id: null,
    zoomLevel: null,
    hoveredId: null,
    placeId: null,
    placeDetailsAreLoading: false,
    validResults: true,
    jurisdictionBoundaryRepIds: [],
    timeline: [],
    heading: {
      fullName: '',
      placeType: '',
    },
    placeDetails: {
      basicInfo: {},
      researchLinks: [],
      historicalInfo: [],
      altNames: [],
      placesWithin: [],
      relatedPlace: [],
      citations: [],
      placeData: [],
    },
  },
}

const EXPANDER_OPEN_DEFAULT = {
  expanders: {
    basicInfo: true,
    // The maps layout starts it closed
    basicInfoMaps: false,
    historicalInfo: false,
    researchLinks: false,
    altNames: false,
    relatedPlaces: false,
    citations: false,
    administrativeBoundaries: false,
    // Record-Keeping Jurisdictions (maps layout) starts open
    recordJurisdictions: true,
  },
  scrollTop: null,
}

/* #endregion */

export type PlaceDetails = {
  basicInfo?: { certainty?: number } & Record<string, unknown>
  historicalInfo?: unknown
  researchLinks?: unknown
  altNames?: unknown
  placesWithin?: unknown
  relatedPlace?: unknown
  relatedPlaces?: unknown
  citations?: unknown
  placeData?: unknown
  displayNames?: unknown
  location?: unknown
  zoomLevel?: unknown
  id?: string | number
} & Record<string, unknown>

export type HoverSource = 'map-popup'

export type SearchDetails = {
  id?: unknown
  placeId?: unknown
  zoomLevel?: unknown
  hoveredId?: unknown
  // Where the hover came from. A 'map-popup' hover highlights the results row and the
  // details period card but doesn't drive the map, which would close the popup being hovered.
  hoverSource?: HoverSource
  placeDetailsAreLoading?: boolean
  validResults?: boolean
  timeline?: unknown
  heading?: unknown
  placeDetails?: PlaceDetails
  historicPlaces?: unknown
  boundary?: unknown
  jurisdictionBoundaryRepIds?: (string | number)[]
  boundaryAvailability?: Record<string, boolean>
  boundariesWithinAvailable?: boolean
  boundariesWithinRepIds?: (string | number)[]
  showBoundariesWithin?: boolean
  borderingRepIds?: (string | number)[]
  borderingSummaries?: BorderingSummary[]
  borderingNoBoundary?: boolean
  highlightedBorderingId?: string | number
  showBorderingBoundaries?: boolean
  skipPan?: boolean
  minZoom?: number
} & Record<string, unknown>

export type DispatchSearch = Dispatch<SearchAction>

export type SearchExpanders = {
  basicInfo: boolean
  basicInfoMaps: boolean
  historicalInfo: boolean
  researchLinks: boolean
  altNames: boolean
  relatedPlaces: boolean
  citations: boolean
  administrativeBoundaries: boolean
  recordJurisdictions: boolean
}

export type SearchState = {
  criteria: Record<string, unknown>
  isLoading: boolean
  noSearchesPerformed: boolean
  validResults: boolean
  results?: unknown
  resultsMap?: Record<string, unknown>
  idList: unknown[]
  resultCount: number
  resultsFound: number
  bordering?: BorderingOverlay | null
  selectedPlace?: { id?: unknown } | null
  toggleToUnfocused: boolean
  filterOverlayIsOpen: boolean
  popoverReopenNonce: number
  mapClickLocation?: Record<string, unknown>
  scrollTop?: number | null
  hoveredId?: unknown
  details: SearchDetails
  expanders: SearchExpanders
} & Record<string, unknown>

export const DEFAULT_SEARCH_STATE: SearchState = {
  ...SEARCH_DEFAULT,
  ...DETAILS_DEFAULT,
  ...EXPANDER_OPEN_DEFAULT,
}

export type SearchAction =
  | { type: 'UPDATE_SEARCH_LOADING'; isLoading: boolean }
  | {
      type: 'UPDATE_SEARCH_RESULTS'
      results: unknown
      resultsMap?: Record<string, unknown>
      idList: unknown[]
      resultCount: number
      resultsFound: number
    }
  | { type: 'CLEAR_RESULTS' }
  | { type: 'BORDERING_FETCH_START'; anchorId: string }
  | {
      type: 'SET_BORDERING'
      anchorId: string
      anchorName?: string
      neighbors: BorderingNeighbor[]
      noBoundary?: boolean
    }
  | { type: 'CLEAR_BORDERING' }
  | { type: 'SUBMITTED_FILTER_OVERLAY' }
  | { type: 'CLEARED_ALL_FILTERS' }
  | { type: 'UPDATE_MAP_CLICK_LOCATION'; mapClickLocation: Record<string, unknown> }
  | { type: 'UPDATE_BOUNDARY'; boundary: unknown }
  | { type: 'UPDATE_HOVERED'; hoveredId: unknown; skipPan: boolean; source?: HoverSource }
  | { type: 'CLEAR_HOVERED'; skipPan?: boolean; minZoom?: number }
  | { type: 'FETCHING' }
  | { type: 'FETCHED'; details?: SearchDetails }
  | { type: 'BASIC_INFO_OPEN'; basicInfo: boolean; mapsLayout?: boolean }
  | { type: 'HISTORICAL_INFO_OPEN'; historicalInfo: boolean }
  | { type: 'RESEARCH_LINKS_OPEN'; researchLinks: boolean }
  | { type: 'ALTERNATE_NAMES_OPEN'; altNames: boolean }
  | { type: 'RELATED_PLACES_OPEN'; relatedPlaces: boolean }
  | { type: 'CITATIONS_OPEN'; citations: boolean }
  | { type: 'ADMINISTRATIVE_BOUNDARIES_OPEN'; administrativeBoundaries: boolean }
  | { type: 'RECORD_JURISDICTIONS_OPEN'; recordJurisdictions: boolean }
  | { type: 'TOGGLE_JURISDICTION_BOUNDARY'; repId: string | number; on: boolean }
  | {
      type: 'SET_BOUNDARY_AVAILABILITY'
      availability: Record<string, boolean>
      focusedRepId: string | number
      storedRepIds?: (string | number)[]
    }
  | {
      type: 'SET_BOUNDARIES_WITHIN'
      focusedRepId: string | number
      childRepIds: (string | number)[]
      storedShowWithin?: boolean
    }
  | { type: 'SET_BOUNDARIES_WITHIN_AVAILABLE'; focusedRepId: string | number; available: boolean }
  | { type: 'TOGGLE_BOUNDARIES_WITHIN'; on: boolean }
  | {
      type: 'SET_BORDERING_BOUNDARIES'
      focusedRepId: string | number
      borderingRepIds: (string | number)[]
      borderingSummaries?: BorderingSummary[]
      // The focused place has no boundary of its own, so borders can't be computed
      noBoundary?: boolean
    }
  | { type: 'TOGGLE_BORDERING_BOUNDARIES'; on: boolean }
  | { type: 'HIGHLIGHT_BORDERING_BOUNDARY'; repId: string | number | null }
  | { type: 'UPDATE_SELECTED_PLACE'; selectedPlace: { id?: unknown; place?: { id?: unknown } } | null | undefined }
  | { type: 'CLEAR_SELECTED_PLACE' }
  | { type: 'REOPEN_POPOVER' }
  | { type: 'SET_SCROLL'; scrollTop: number | null }

function applyFetchedDetails(state: SearchState, action: Extract<SearchAction, { type: 'FETCHED' }>): SearchState {
  const { id, zoomLevel, placeId, timeline, heading, placeDetails, historicPlaces } = action.details ?? {}
  // Boundary toggles are per-place: reset when the focused place changes, keep on same-id refetch
  const isSamePlace = String(id) === String(state.details.id)
  const details = {
    ...state.details,
    placeDetailsAreLoading: false,
    id,
    zoomLevel,
    placeId,
    timeline,
    heading,
    placeDetails,
    historicPlaces,
    jurisdictionBoundaryRepIds: isSamePlace ? (state.details.jurisdictionBoundaryRepIds ?? []) : [],
    boundaryAvailability: isSamePlace ? state.details.boundaryAvailability : undefined,
    boundariesWithinAvailable: isSamePlace ? state.details.boundariesWithinAvailable : undefined,
    boundariesWithinRepIds: isSamePlace ? state.details.boundariesWithinRepIds : undefined,
    showBoundariesWithin: isSamePlace ? (state.details.showBoundariesWithin ?? false) : false,
    borderingRepIds: isSamePlace ? state.details.borderingRepIds : undefined,
    borderingSummaries: isSamePlace ? state.details.borderingSummaries : undefined,
    borderingNoBoundary: isSamePlace ? state.details.borderingNoBoundary : undefined,
    highlightedBorderingId: isSamePlace ? state.details.highlightedBorderingId : undefined,
    showBorderingBoundaries: isSamePlace ? (state.details.showBorderingBoundaries ?? false) : false,
  }

  debugVerbose(`details: ${JSON.stringify(details)}`)
  return { ...state, details }
}

function toggleJurisdictionBoundary(
  state: SearchState,
  action: Extract<SearchAction, { type: 'TOGGLE_JURISDICTION_BOUNDARY' }>
): SearchState {
  const current = state.details.jurisdictionBoundaryRepIds ?? []
  const withoutRepId = current.filter((repId) => String(repId) !== String(action.repId))
  const jurisdictionBoundaryRepIds = action.on ? [...withoutRepId, action.repId] : withoutRepId
  return { ...state, details: { ...state.details, jurisdictionBoundaryRepIds } }
}

function applyBoundaryAvailability(
  state: SearchState,
  action: Extract<SearchAction, { type: 'SET_BOUNDARY_AVAILABILITY' }>
): SearchState {
  // Ignore stale responses for a previously focused place
  if (String(action.focusedRepId) !== String(state.details.id)) return { ...state }
  // Seed only on first arrival for this place: restore the session-cached toggle
  // set when one exists (filtered to still-available boundaries), otherwise default
  // the focused place's boundary on when it has one. Never clobbers later user toggling.
  const isFirstArrival = state.details.boundaryAvailability === undefined
  const defaultRepIds = action.availability[String(action.focusedRepId)] ? [action.focusedRepId] : []
  const seededRepIds = action.storedRepIds
    ? action.storedRepIds.filter((repId) => action.availability[String(repId)])
    : defaultRepIds
  return {
    ...state,
    details: {
      ...state.details,
      boundaryAvailability: action.availability,
      jurisdictionBoundaryRepIds: isFirstArrival ? seededRepIds : (state.details.jurisdictionBoundaryRepIds ?? []),
    },
  }
}

function applyBoundariesWithin(
  state: SearchState,
  action: Extract<SearchAction, { type: 'SET_BOUNDARIES_WITHIN' }>
): SearchState {
  // Ignore stale responses for a previously focused place
  if (String(action.focusedRepId) !== String(state.details.id)) return { ...state }
  // Seed the toggle from the session-cached preference only on first arrival (and
  // only when there is something to show). Seeding can only turn the toggle ON —
  // the user may have toggled it on while the list was being fetched (the lazy
  // full-probe path), and that must never be flipped back off here.
  const isFirstArrival = state.details.boundariesWithinRepIds === undefined
  const currentShowWithin = state.details.showBoundariesWithin ?? false
  const seededShowWithin =
    currentShowWithin || Boolean(isFirstArrival && action.childRepIds.length > 0 && action.storedShowWithin)
  return {
    ...state,
    details: {
      ...state.details,
      boundariesWithinRepIds: action.childRepIds,
      boundariesWithinAvailable: action.childRepIds.length > 0,
      showBoundariesWithin: seededShowWithin,
    },
  }
}

export default function searchReducer(state: SearchState, action: SearchAction): SearchState {
  debugVerbose('searchReducer:', state)
  debug('action:', action)

  let newState = { ...state }
  switch (action.type) {
    /* #region Search */
    case 'UPDATE_SEARCH_LOADING':
      newState = { ...state, isLoading: action.isLoading }
      break
    case 'UPDATE_SEARCH_RESULTS':
      newState = {
        ...state,
        results: action.results,
        resultsMap: action.resultsMap,
        idList: action.idList,
        resultCount: action.resultCount,
        resultsFound: action.resultsFound,
        noSearchesPerformed: false,
        isLoading: false,
        selectedPlace: null,
        toggleToUnfocused: false,
        details: {
          ...state.details,
          skipPan: false, // Clear skipPan on new search results to allow fitToChildren
        },
      }
      break
    case 'CLEAR_RESULTS':
      newState = {
        ...state,
        results: [],
        idList: [],
        resultCount: 0,
        resultsFound: 0,
        noSearchesPerformed: true,
      }
      break
    // The overlay is independent of the result set: CLEAR_RESULTS leaves it alone
    case 'BORDERING_FETCH_START':
      newState = { ...state, bordering: { anchorId: action.anchorId, neighbors: [], isLoading: true } }
      break
    case 'SET_BORDERING':
      // Ignore stale responses for a previously requested anchor
      if (String(action.anchorId) !== String(state.bordering?.anchorId)) break
      newState = {
        ...state,
        bordering: {
          anchorId: action.anchorId,
          anchorName: action.anchorName,
          neighbors: action.neighbors,
          noBoundary: action.noBoundary,
          isLoading: false,
        },
      }
      break
    case 'CLEAR_BORDERING':
      newState = { ...state, bordering: null }
      break
    /* #endregion */

    /* #region SearchFilter */
    case 'SUBMITTED_FILTER_OVERLAY':
    case 'CLEARED_ALL_FILTERS':
      newState = {
        ...state,
        filterOverlayIsOpen: false,
      }
      break
    /* #endregion */

    /* #region PlacesMap */
    case 'UPDATE_MAP_CLICK_LOCATION':
      {
        const mapClickLocation = { ...state.mapClickLocation, ...action.mapClickLocation }
        newState = { ...state, mapClickLocation }
      }

      break
    case 'UPDATE_BOUNDARY':
      {
        const details = { ...state.details, boundary: action.boundary }
        newState = { ...state, details }
      }

      break
    case 'UPDATE_HOVERED':
      {
        const details = {
          ...state.details,
          hoveredId: action.hoveredId,
          hoverSource: action.source,
          skipPan: action.skipPan,
          minZoom: undefined, // Clear minZoom on hover (don't zoom for hover)
        }
        newState = { ...state, details }
      }

      break
    case 'CLEAR_HOVERED':
      {
        // When clearing hover, optionally set minZoom if provided (for focus-from-list)
        // Otherwise undefined (for hover clear, X button, etc.)
        const details = {
          ...state.details,
          hoveredId: null,
          hoverSource: undefined,
          skipPan: action.skipPan,
          minZoom: action.minZoom,
        }
        newState = { ...state, details }
      }

      break
    /* #endregion */

    /* #region fetching */
    case 'FETCHING':
      {
        const details = { ...state.details, placeDetailsAreLoading: true }
        newState = { ...state, details }
      }

      break
    case 'FETCHED':
      newState = applyFetchedDetails(state, action)
      break
    /* #endregion */

    /* #region Place Details */
    case 'BASIC_INFO_OPEN':
      {
        const key = action.mapsLayout ? 'basicInfoMaps' : 'basicInfo'
        const expanders = { ...state.expanders, [key]: action.basicInfo }
        newState = { ...state, expanders }
      }

      break
    case 'HISTORICAL_INFO_OPEN':
      {
        const expanders = { ...state.expanders, historicalInfo: action.historicalInfo }
        newState = { ...state, expanders }
      }

      break
    case 'RESEARCH_LINKS_OPEN':
      {
        const expanders = { ...state.expanders, researchLinks: action.researchLinks }
        newState = { ...state, expanders }
      }

      break
    case 'ALTERNATE_NAMES_OPEN':
      {
        const expanders = { ...state.expanders, altNames: action.altNames }
        newState = { ...state, expanders }
      }

      break
    case 'RELATED_PLACES_OPEN':
      {
        const expanders = { ...state.expanders, relatedPlaces: action.relatedPlaces }
        newState = { ...state, expanders }
      }

      break
    case 'CITATIONS_OPEN':
      {
        const expanders = { ...state.expanders, citations: action.citations }
        newState = { ...state, expanders }
      }

      break
    case 'ADMINISTRATIVE_BOUNDARIES_OPEN':
      {
        const expanders = { ...state.expanders, administrativeBoundaries: action.administrativeBoundaries }
        newState = { ...state, expanders }
      }

      break
    case 'RECORD_JURISDICTIONS_OPEN':
      newState = { ...state, expanders: { ...state.expanders, recordJurisdictions: action.recordJurisdictions } }
      break
    case 'TOGGLE_JURISDICTION_BOUNDARY':
      newState = toggleJurisdictionBoundary(state, action)
      break
    case 'SET_BOUNDARY_AVAILABILITY':
      newState = applyBoundaryAvailability(state, action)
      break
    case 'SET_BOUNDARIES_WITHIN':
      newState = applyBoundariesWithin(state, action)
      break
    case 'SET_BOUNDARIES_WITHIN_AVAILABLE':
      // Ignore stale responses for a previously focused place
      if (String(action.focusedRepId) !== String(state.details.id)) break
      newState = { ...state, details: { ...state.details, boundariesWithinAvailable: action.available } }
      break
    case 'TOGGLE_BOUNDARIES_WITHIN':
      newState = { ...state, details: { ...state.details, showBoundariesWithin: action.on } }
      break
    case 'SET_BORDERING_BOUNDARIES':
      // Ignore stale responses for a previously focused place
      if (String(action.focusedRepId) !== String(state.details.id)) break
      newState = {
        ...state,
        details: {
          ...state.details,
          borderingRepIds: action.borderingRepIds,
          borderingSummaries: action.borderingSummaries,
          borderingNoBoundary: action.noBoundary,
        },
      }
      break
    case 'TOGGLE_BORDERING_BOUNDARIES':
      newState = { ...state, details: { ...state.details, showBorderingBoundaries: action.on } }
      break
    case 'HIGHLIGHT_BORDERING_BOUNDARY':
      newState = { ...state, details: { ...state.details, highlightedBorderingId: action.repId ?? undefined } }
      break
    /* #endregion */

    case 'UPDATE_SELECTED_PLACE':
      {
        const toggleToUnfocused = Boolean(
          state?.selectedPlace?.id && state?.selectedPlace?.id === action.selectedPlace?.id
        )
        const selectedPlace = toggleToUnfocused ? null : action.selectedPlace
        const hoveredId = toggleToUnfocused ? null : action.selectedPlace?.id || null
        debug(
          `toggleToUnfocused: ${toggleToUnfocused}, selectedPlace: ${JSON.stringify(
            selectedPlace
          )}, hoveredId: ${hoveredId}`
        )
        newState = { ...state, selectedPlace, hoveredId, toggleToUnfocused }
      }
      break
    case 'CLEAR_SELECTED_PLACE':
      {
        const details = { ...state.details, minZoom: undefined }
        newState = { ...state, selectedPlace: null, toggleToUnfocused: false, details }
      }
      break

    case 'REOPEN_POPOVER':
      newState = { ...state, popoverReopenNonce: (state.popoverReopenNonce ?? 0) + 1 }
      break

    case 'SET_SCROLL':
      newState = { ...state, scrollTop: action.scrollTop }
      break
    default:
      throw new Error(`Unhandled action type: ${(action as { type: string }).type}`)
  }
  debugVerbose('newSearchState:', newState)
  return newState
}
